import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Share2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import BlogLikeButton from '../components/BlogLikeButton';
import BlogDisclaimer from '../components/blog/BlogDisclaimer';
import useTopicKeywordLinker from '../hooks/useTopicKeywordLinker';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shared, setShared] = useState(false);
  const { renderLinkedHTML } = useTopicKeywordLinker();

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/blog/posts/${id}`);
      setPost(response.data);
      
      // Fetch related posts from same category
      if (response.data.category) {
        const relatedResponse = await axiosInstance.get(
          `/blog/posts?category=${encodeURIComponent(response.data.category)}&limit=3`
        );
        setRelatedPosts(
          relatedResponse.data.filter(p => p.id !== id).slice(0, 2)
        );
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      toast.error('Beitrag nicht gefunden');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url
        });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link in Zwischenablage kopiert!');
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      } catch (err) {
        toast.error('Konnte Link nicht kopieren');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0EB' }}>
      {/* Hero Image */}
      {post.image && (
        <div className="w-full h-64 md:h-96 relative">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button + Share */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#8B7355] hover:text-[#6D5A47]"
            data-testid="blog-back-to-list"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zum Blog
          </Link>
          <Button
            onClick={handleShare}
            variant="outline"
            className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#C4A084] hover:text-white"
            data-testid="blog-share-button"
          >
            {shared ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Kopiert
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4 mr-2" />
                Teilen
              </>
            )}
          </Button>
        </div>

        <article className="bg-white shadow-sm overflow-hidden">
          <div className="p-6 md:p-10">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
              {post.category && (
                <span className="px-3 py-1 bg-[#F5F0EB] text-[#8B7355] font-medium">
                  {post.category}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.created_at)}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-sm text-gray-600"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:mt-10 prose-headings:mb-4 prose-p:text-gray-600 prose-p:my-5 prose-p:leading-relaxed prose-a:text-[#8B7355] blog-content"
              dangerouslySetInnerHTML={{ __html: renderLinkedHTML(post.content) }}
            />

            {/* Like / Share row below content */}
            <div className="mt-12 pt-6 border-t border-[#E8DCC8] flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <BlogLikeButton postId={post.id} initialLikes={post.likes || 0} />
                <span className="text-xs text-[#8B7355]">Gefällt dir der Beitrag?</span>
              </div>
              <Button
                onClick={handleShare}
                variant="outline"
                className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#C4A084] hover:text-white"
              >
                {shared ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Kopiert
                  </>
                ) : (
                  <>
                    <Share2 className="h-4 w-4 mr-2" />
                    Teilen
                  </>
                )}
              </Button>
            </div>

            {/* Legal disclaimer */}
            <BlogDisclaimer />
          </div>
        </article>

        {/* Inline styles for HTML-content images & lists */}
        <style>{`
          .blog-content img {
            display: block;
            width: 100%;
            max-width: 720px;
            height: auto;
            margin: 2.5rem auto;
            border-radius: 4px;
            box-shadow: 0 6px 24px rgba(93, 78, 60, 0.12);
          }
          @media (min-width: 768px) {
            .blog-content img { margin: 3.5rem auto; }
          }
          .blog-content h2 {
            margin-top: 2.5rem;
            margin-bottom: 1rem;
            font-size: 1.5rem;
            color: #3D3D3D;
          }
          .blog-content h3 {
            margin-top: 2rem;
            margin-bottom: 0.75rem;
            font-size: 1.25rem;
            color: #3D3D3D;
          }
          .blog-content p {
            margin-top: 1.25rem;
            margin-bottom: 1.25rem;
            line-height: 1.75;
          }
          .blog-content ul, .blog-content ol {
            margin-top: 1.25rem;
            margin-bottom: 1.5rem;
            padding-left: 1.5rem;
          }
          .blog-content li {
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
            line-height: 1.6;
          }
          /* Collapsible sections inside blog posts */
          .blog-content details {
            border: 1px solid #E8DCC8;
            background: #FAF5EE;
            padding: 1rem 1.25rem;
            margin: 0.75rem 0;
            transition: background-color 0.2s ease;
          }
          .blog-content details[open] {
            background: #ffffff;
          }
          .blog-content details summary {
            cursor: pointer;
            font-size: 1.05rem;
            color: #5D4E3C;
            list-style: none;
            position: relative;
            padding-right: 1.5rem;
            user-select: none;
          }
          .blog-content details summary::-webkit-details-marker { display: none; }
          .blog-content details summary::after {
            content: '+';
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            font-size: 1.4rem;
            color: #C4A084;
            line-height: 1;
            font-weight: 300;
          }
          .blog-content details[open] summary::after { content: '−'; }
          .blog-content details > * + * { margin-top: 0.75rem; }
          .blog-content details p,
          .blog-content details ul,
          .blog-content details ol { margin-top: 0.75rem; }
        `}</style>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Ähnliche Beiträge</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.id}`}
                  className="bg-white overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                >
                  <div className="aspect-video overflow-hidden">
                    {relatedPost.image ? (
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#C4A084] to-[#8B7355]" />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 group-hover:text-[#8B7355] line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(relatedPost.created_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
